import React from 'react';
import { Box, Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { Markdown } from '@uplift/ui-v2';

const useStyles = makeStyles({ name: 'SupportArticleMarkdown' })(theme => ({
  root: {
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      display: 'block',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
    },
    '& a': {
      color: theme.palette.secondary.main,
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse' as const,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      display: 'block',
      overflowX: 'auto' as const,
    },
    '& th': {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 1.5),
      textAlign: 'left' as const,
      fontWeight: 600,
      backgroundColor: theme.palette.grey[50],
    },
    '& td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 1.5),
      textAlign: 'left' as const,
    },
    '& p': {
      marginTop: 0,
      marginBottom: theme.spacing(1.5),
    },
    '& ul, & ol': {
      marginTop: 0,
      marginBottom: theme.spacing(1.5),
      '& p': {
        marginTop: 0,
        marginBottom: 0,
      },
    },
    '& li': {
      marginBottom: theme.spacing(0.75),
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& blockquote': {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
  heading: {
    scrollMarginTop: theme.spacing(12),
  },
}));

const getNodeText = (node: React.ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(getNodeText).join('');
  }

  if (React.isValidElement(node)) {
    return getNodeText(node.props.children);
  }

  return '';
};

export const slugifyHeading = (text: string): string =>
  text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

type SupportArticleMarkdownProps = {
  children: string;
};

export const SupportArticleMarkdown: React.FC<SupportArticleMarkdownProps> = ({ children }) => {
  const { classes } = useStyles();

  const scrollToHash = (hash: string) => {
    const id = hash.replace(/^#/, '');
    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const renderHeading = (
    component: 'h2' | 'h3' | 'h4',
    variant: 'h4' | 'h6' | 'subtitle1',
    marginTop: number,
    marginBottom: number,
  ) =>
    ({ children: headingChildren }: { children?: React.ReactNode }) => {
      const text = getNodeText(headingChildren);
      const id = slugifyHeading(text);

      return (
        <Typography
          id={id || undefined}
          variant={variant}
          component={component}
          className={classes.heading}
          sx={{
            // Override shared Markdown heading margins (marginTop: 0).
            marginTop: (theme) => `${theme.spacing(marginTop)} !important`,
            marginBottom: (theme) => `${theme.spacing(marginBottom)} !important`,
            ...(variant === 'subtitle1' && { fontWeight: 600 }),
            '&:first-child': {
              marginTop: '0 !important',
            },
          }}
        >
          {headingChildren}
        </Typography>
      );
    };

  return (
    <Box className={classes.root}>
      <Markdown
        components={{
          h2: renderHeading('h2', 'h4', 4, 1.5),
          h3: renderHeading('h3', 'h6', 3, 1),
          h4: renderHeading('h4', 'subtitle1', 2.5, 0.75),
          a: ({ href, children: linkChildren }) => {
            if (href?.startsWith('#')) {
              return (
                <MuiLink
                  href={href}
                  underline="hover"
                  onClick={(event) => {
                    event.preventDefault();
                    scrollToHash(href);
                    window.history.replaceState(null, '', href);
                  }}
                >
                  {linkChildren}
                </MuiLink>
              );
            }

            if (href?.startsWith('/')) {
              return (
                <MuiLink component={RouterLink} to={href}>
                  {linkChildren}
                </MuiLink>
              );
            }

            return (
              <MuiLink href={href} target="_blank" rel="noopener noreferrer">
                {linkChildren}
              </MuiLink>
            );
          },
        }}
      >
        {children}
      </Markdown>
    </Box>
  );
};

export const createMarkdownArticle = (content: string): React.FC => {
  const MarkdownArticle: React.FC = () => <SupportArticleMarkdown>{content}</SupportArticleMarkdown>;

  return MarkdownArticle;
};
